import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import type { Employee, Nom35Assessment, ClimateAssessment, AssessmentCampaign } from '@/types';
import { Trash2, RotateCcw, Pencil, MessageCircle, Send, RefreshCw, Eye, FileDown, BarChart3, MoreVertical, Search, Mail, PlusCircle, History, XCircle, Upload, Users, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Badge, getRiskLevelVariant } from '@/components/ui/Badge';
import { generateNom35ExpedientePdf, generateNom35GlobalPdf } from '@/lib/nom35PdfReport';
import { EmployeeHistoryDialog } from '@/components/app/directory/EmployeeHistoryDialog';
import { BitacoraEntryPopover } from '@/components/app/bitacora/BitacoraEntryPopover';
import { getNom35GuidesToSend } from '@/lib/nom35';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/DropdownMenu';

type TabId = 'directorio' | 'bitacora' | 'evaluaciones' | 'clima' | 'cultura' | 'papelera';

/** Empleado parseado del CSV (sin id, para previsualización antes de guardar). */
interface ParsedEmployee {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  position?: string;
  department?: string;
  dateOfHire: string;
}

const CSV_PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'san', 'santa', 'mac']);

/** Parsea nombre completo según convención mexicana (partículas agrupadas con la palabra siguiente). */
function parseFullName(fullName: string): { firstName: string; lastName: string } {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { firstName: '', lastName: '' };
  if (words.length === 1) return { firstName: words[0], lastName: '' };

  const blocks: string[] = [];
  let i = 0;
  while (i < words.length) {
    const w = words[i].toLowerCase();
    if (CSV_PARTICLES.has(w) && i + 1 < words.length) {
      blocks.push(`${words[i]} ${words[i + 1]}`);
      i += 2;
    } else {
      blocks.push(words[i]);
      i += 1;
    }
  }

  if (blocks.length === 2) {
    return { firstName: blocks[0], lastName: blocks[1] };
  }
  if (blocks.length === 3) {
    return { firstName: blocks[0], lastName: `${blocks[1]} ${blocks[2]}` };
  }
  if (blocks.length >= 4) {
    return {
      firstName: `${blocks[0]} ${blocks[1]}`,
      lastName: blocks.slice(2).join(' '),
    };
  }
  return { firstName: blocks[0] ?? '', lastName: blocks.slice(1).join(' ') };
}

export function Nom035Page() {
  const { appUser } = useAuth();
  const companyIdOrNull = appUser?.companyId ?? null;

  const [activeTab, setActiveTab] = useState<TabId>('directorio');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [hardDeletingId, setHardDeletingId] = useState<string | null>(null);
  const [sendingAssessmentId, setSendingAssessmentId] = useState<string | null>(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [currentResults, setCurrentResults] = useState<Nom35Assessment[] | null>(null);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  const [completedAssessments, setCompletedAssessments] = useState<Nom35Assessment[]>([]);
  const [climateAssessments, setClimateAssessments] = useState<ClimateAssessment[]>([]);
  const [sendingClimateId, setSendingClimateId] = useState<string | null>(null);
  const [climateSendAllDialogOpen, setClimateSendAllDialogOpen] = useState(false);
  const [isSendingClimateToAll, setIsSendingClimateToAll] = useState(false);
  const [activeClimateCampaign, setActiveClimateCampaign] = useState<AssessmentCampaign | null>(null);
  const [loadingClimateCampaign, setLoadingClimateCampaign] = useState(true);
  const [closedClimateCampaigns, setClosedClimateCampaigns] = useState<AssessmentCampaign[]>([]);
  const [climateHistoryModalOpen, setClimateHistoryModalOpen] = useState(false);
  const [loadingClimateHistory, setLoadingClimateHistory] = useState(false);
  const [newClimateCampaignModalOpen, setNewClimateCampaignModalOpen] = useState(false);
  const [newClimateCampaignTitle, setNewClimateCampaignTitle] = useState('');
  const [creatingClimateCampaign, setCreatingClimateCampaign] = useState(false);
  const [closingClimateId, setClosingClimateId] = useState<string | null>(null);
  const [climateEmployeeSearch, setClimateEmployeeSearch] = useState('');
  const [climateSelectedEmployee, setClimateSelectedEmployee] = useState<Employee | null>(null);
  const [climateDropdownOpen, setClimateDropdownOpen] = useState(false);
  const [activeCultureCampaign, setActiveCultureCampaign] = useState<AssessmentCampaign | null>(null);
  const [loadingCultureCampaign, setLoadingCultureCampaign] = useState(true);
  const [closedCultureCampaigns, setClosedCultureCampaigns] = useState<AssessmentCampaign[]>([]);
  const [cultureHistoryModalOpen, setCultureHistoryModalOpen] = useState(false);
  const [loadingCultureHistory, setLoadingCultureHistory] = useState(false);
  const [newCultureCampaignModalOpen, setNewCultureCampaignModalOpen] = useState(false);
  const [newCultureCampaignTitle, setNewCultureCampaignTitle] = useState('');
  const [creatingCultureCampaign, setCreatingCultureCampaign] = useState(false);
  const [closingCultureCampaignId, setClosingCultureCampaignId] = useState<string | null>(null);
  const [cultureInvites, setCultureInvites] = useState<
    Array<{
      id: string;
      campaignId?: string;
      employeeId?: string;
      employeeName?: string;
      email?: string;
      department?: string;
      status?: string;
      createdAt?: string;
    }>
  >([]);
  const [loadingCultureInvites, setLoadingCultureInvites] = useState(false);
  const [bitacoraSearch, setBitacoraSearch] = useState('');
  const [isGeneratingGlobalPdf, setIsGeneratingGlobalPdf] = useState(false);
  const [isGeneratingExpedientePdf, setIsGeneratingExpedientePdf] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState<{ id: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [directoryNom35Selected, setDirectoryNom35Selected] = useState<Employee | null>(null);
  const [directoryNom35DropdownOpen, setDirectoryNom35DropdownOpen] = useState(false);
  const [sendingNom35LinkId, setSendingNom35LinkId] = useState<string | null>(null);
  const [sendAllDialogOpen, setSendAllDialogOpen] = useState(false);
  const [isSendingToAll, setIsSendingToAll] = useState(false);
  const [companyCountry, setCompanyCountry] = useState<string>('MX');
  const [activeCampaign, setActiveCampaign] = useState<AssessmentCampaign | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [newCampaignModalOpen, setNewCampaignModalOpen] = useState(false);
  const [newCampaignTitle, setNewCampaignTitle] = useState('');
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [closingCampaignId, setClosingCampaignId] = useState<string | null>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [closedCampaigns, setClosedCampaigns] = useState<AssessmentCampaign[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [parsedEmployees, setParsedEmployees] = useState<ParsedEmployee[]>([]);
  const [importingFile, setImportingFile] = useState(false);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  // Solo la evaluación más reciente por empleado para el Dashboard de Evaluaciones
  const latestAssessments = useMemo(() => {
    const map = new Map<string, Nom35Assessment>();
    completedAssessments.forEach((assessment) => {
      const existing = map.get(assessment.employeeId);
      if (!existing) {
        map.set(assessment.employeeId, assessment);
      } else if (assessment.completedAt && existing.completedAt) {
        if (new Date(assessment.completedAt) > new Date(existing.completedAt)) {
          map.set(assessment.employeeId, assessment);
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [completedAssessments]);

  const latestAssessmentByEmployee = useMemo(() => {
    const m = new Map<string, Nom35Assessment>();
    latestAssessments.forEach((a) => m.set(a.employeeId, a));
    return m;
  }, [latestAssessments]);

  const completedEmployeeIds = useMemo(
    () => new Set(completedAssessments.map((a) => a.employeeId).filter(Boolean)),
    [completedAssessments]
  );

  const climateFilteredEmployees = useMemo(() => {
    const q = climateEmployeeSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q) ||
        (e.department ?? '').toLowerCase().includes(q)
    );
  }, [employees, climateEmployeeSearch]);

  const climateAssessmentsForDisplay = useMemo(() => {
    if (!activeClimateCampaign) return climateAssessments;
    return climateAssessments.filter((a) => a.campaignId === activeClimateCampaign.id);
  }, [climateAssessments, activeClimateCampaign]);

  const cultureInvitesForDisplay = useMemo(() => {
    if (!activeCultureCampaign) return [];
    return cultureInvites.filter((inv) => inv.campaignId === activeCultureCampaign.id);
  }, [cultureInvites, activeCultureCampaign]);

  const bitacoraFilteredEmployees = useMemo(() => {
    const q = bitacoraSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        (e.department ?? '').toLowerCase().includes(q)
    );
  }, [employees, bitacoraSearch]);

  function getEmployeeRiskLevel(emp: Employee): 'sin_riesgo' | 'requiere_atencion' | 'sin_datos' {
    if (!completedEmployeeIds.has(emp.id)) return 'sin_datos';
    const assessment = latestAssessmentByEmployee.get(emp.id);
    if (!assessment) return 'sin_datos';
    if (assessment.guideType === 'NOM35_G1' && assessment.requiresClinicalAttention === true) {
      return 'requiere_atencion';
    }
    const level = (assessment.resultLevel ?? '').toLowerCase().replace(/\s/g, '');
    if (level === 'bajo' || level === 'nulo') return 'sin_riesgo';
    if (level === 'alto' || level === 'muyalto') return 'requiere_atencion';
    const score = assessment.resultScore ?? -1;
    if (score >= 0 && score < 20) return 'sin_riesgo';
    if (score >= 20) return 'requiere_atencion';
    return 'sin_riesgo';
  }

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (emp) =>
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(q) ||
        (emp.email ?? '').toLowerCase().includes(q) ||
        (emp.position ?? '').toLowerCase().includes(q) ||
        (emp.department ?? '').toLowerCase().includes(q)
    );
  }, [employees, searchQuery]);

  const pendingCount = useMemo(
    () => employees.filter((e) => !completedEmployeeIds.has(e.id)).length,
    [employees, completedEmployeeIds]
  );
  const hasAnyCompleted = useMemo(
    () => employees.some((e) => completedEmployeeIds.has(e.id)),
    [employees, completedEmployeeIds]
  );

  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState<string | undefined>('');
  const [formPosition, setFormPosition] = useState('');
  const [formDepartment, setFormDepartment] = useState('');
  const [formDateOfHire, setFormDateOfHire] = useState('');
  const [formSex, setFormSex] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formMaritalStatus, setFormMaritalStatus] = useState('');
  const [formEducationLevel, setFormEducationLevel] = useState('');
  const [formJobType, setFormJobType] = useState('');
  const [formContractType, setFormContractType] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const loadEmployees = useCallback(async () => {
    if (!companyIdOrNull) {
      setLoading(false);
      setEmployees([]);
      setCompletedAssessments([]);
      return;
    }
    setLoading(true);
    setError(null);
    setEmployees([]);
    try {
      const col = collection(db, 'employees');
      const isArchived = activeTab === 'papelera';
      const q = query(
        col,
        where('companyId', '==', companyIdOrNull),
        where('isActive', '==', !isArchived)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        companyId: d.data().companyId as string,
        firstName: d.data().firstName as string,
        lastName: d.data().lastName as string,
        email: d.data().email as string,
        phone: d.data().phone as string | undefined,
        dateOfHire: (d.data().dateOfHire as string) ?? '',
        isActive: d.data().isActive !== false,
        archivedAt: d.data().archivedAt as string | undefined,
      })) as Employee[];
      list.sort((a, b) => {
        const cmp = (a.lastName ?? '').localeCompare(b.lastName ?? '', 'es', { sensitivity: 'base' });
        return cmp !== 0 ? cmp : (a.firstName ?? '').localeCompare(b.firstName ?? '', 'es', { sensitivity: 'base' });
      });

      let completedAssessmentsList: Nom35Assessment[] = [];
      if (activeCampaign) {
        try {
          const assessmentsSnap = await getDocs(
            query(
              collection(db, 'nom35_assessments'),
              where('companyId', '==', companyIdOrNull),
              where('campaignId', '==', activeCampaign.id),
              where('status', '==', 'completed')
            )
          );
          completedAssessmentsList = assessmentsSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            companyId: data.companyId as string,
            campaignId: (data.campaignId as string) ?? activeCampaign.id,
            employeeId: data.employeeId as string,
            employeeName: data.employeeName as string,
            guideType: data.guideType as Nom35Assessment['guideType'],
            status: data.status as 'completed',
            createdAt: data.createdAt as string,
            completedAt: data.completedAt as string | undefined,
            answers: data.answers as Record<string, number> | undefined,
            resultScore: data.resultScore as number | undefined,
            resultLevel: data.resultLevel as string | undefined,
            requiresClinicalAttention: data.requiresClinicalAttention as boolean | undefined,
          } as Nom35Assessment;
        });
          completedAssessmentsList.sort((a, b) => {
            const dateA = a.completedAt || a.createdAt || '';
            const dateB = b.completedAt || b.createdAt || '';
            return dateB.localeCompare(dateA);
          });
        } catch {
          // Ignorar errores al cargar evaluaciones
        }
      }

      try {
        const climateSnap = await getDocs(
          query(
            collection(db, 'climate_assessments'),
            where('companyId', '==', companyIdOrNull)
          )
        );
        const climateList = climateSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            companyId: data.companyId as string,
            campaignId: (data.campaignId as string) ?? 'temp-campaign-id',
            employeeId: data.employeeId as string | undefined,
            employeeName: data.employeeName as string | undefined,
            status: data.status as 'pending' | 'completed',
            createdAt: data.createdAt as string,
            completedAt: data.completedAt as string | undefined,
          } as ClimateAssessment;
        });
        climateList.sort((a, b) => {
          const dateA = a.completedAt || a.createdAt || '';
          const dateB = b.completedAt || b.createdAt || '';
          return dateB.localeCompare(dateA);
        });
        setClimateAssessments(climateList);
      } catch (climateErr) {
        console.warn('Error cargando encuestas de clima:', climateErr);
        setClimateAssessments([]);
      }

      setEmployees(list);
      setCompletedAssessments(completedAssessmentsList);
    } catch (err) {
      console.error('Error cargando empleados', err);
      setError(err instanceof Error ? err.message : 'Error al cargar empleados');
      setCompletedAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [companyIdOrNull, activeTab, activeCampaign]);

  useEffect(() => {
    if (!companyIdOrNull) return;
    getDoc(doc(db, 'companies', companyIdOrNull)).then((snap) => {
      if (snap.exists()) {
        setCompanyCountry((snap.data().country as string) ?? 'MX');
      }
    });
  }, [companyIdOrNull]);

  useEffect(() => {
    if (!companyIdOrNull) {
      setActiveCampaign(null);
      setLoadingCampaign(false);
      return;
    }
    setLoadingCampaign(true);
    getDocs(
      query(
        collection(db, 'assessment_campaigns'),
        where('companyId', '==', companyIdOrNull),
        where('type', '==', 'NOM35'),
        where('status', '==', 'active')
      )
    )
      .then((snap) => {
        try {
          const campaignDoc = snap.docs[0];
          if (campaignDoc) {
            const data = campaignDoc.data();
            const createdAt = data.createdAt;
            let createdAtStr = new Date().toISOString();
            if (createdAt) {
              const ts = createdAt as { toDate?: () => Date };
              if (typeof ts.toDate === 'function') {
                createdAtStr = ts.toDate().toISOString();
              } else if (typeof createdAt === 'string') {
                createdAtStr = createdAt;
              }
            }
            setActiveCampaign({
              id: campaignDoc.id,
              companyId: data.companyId as string,
              title: data.title as string,
              type: 'NOM35',
              status: 'active',
              createdAt: createdAtStr,
            });
          } else {
            setActiveCampaign(null);
          }
        } catch {
          setActiveCampaign(null);
        }
      })
      .catch(() => setActiveCampaign(null))
      .finally(() => setLoadingCampaign(false));
  }, [companyIdOrNull]);

  const loadClimateCampaign = useCallback(() => {
    if (!companyIdOrNull) {
      setActiveClimateCampaign(null);
      setLoadingClimateCampaign(false);
      return;
    }
    setLoadingClimateCampaign(true);
    getDocs(
      query(
        collection(db, 'assessment_campaigns'),
        where('companyId', '==', companyIdOrNull),
        where('type', '==', 'CLIMATE'),
        where('status', '==', 'active')
      )
    )
      .then((snap) => {
        try {
          const campaignDoc = snap.docs[0];
          if (campaignDoc) {
            const data = campaignDoc.data();
            const createdAt = data.createdAt;
            let createdAtStr = new Date().toISOString();
            if (createdAt) {
              const ts = createdAt as { toDate?: () => Date };
              if (typeof ts.toDate === 'function') {
                createdAtStr = ts.toDate().toISOString();
              } else if (typeof createdAt === 'string') {
                createdAtStr = createdAt;
              }
            }
            setActiveClimateCampaign({
              id: campaignDoc.id,
              companyId: data.companyId as string,
              title: data.title as string,
              type: 'CLIMATE',
              status: 'active',
              createdAt: createdAtStr,
            });
          } else {
            setActiveClimateCampaign(null);
          }
        } catch {
          setActiveClimateCampaign(null);
        }
      })
      .catch(() => setActiveClimateCampaign(null))
      .finally(() => setLoadingClimateCampaign(false));
  }, [companyIdOrNull]);

  useEffect(() => {
    loadClimateCampaign();
  }, [loadClimateCampaign]);

  const loadCultureCampaign = useCallback(() => {
    if (!companyIdOrNull) {
      setActiveCultureCampaign(null);
      setLoadingCultureCampaign(false);
      return;
    }
    setLoadingCultureCampaign(true);
    getDocs(
      query(
        collection(db, 'assessment_campaigns'),
        where('companyId', '==', companyIdOrNull),
        where('type', '==', 'CULTURE'),
        where('status', '==', 'active')
      )
    )
      .then((snap) => {
        try {
          const campaignDoc = snap.docs[0];
          if (campaignDoc) {
            const data = campaignDoc.data();
            const createdAt = data.createdAt;
            let createdAtStr = new Date().toISOString();
            if (createdAt) {
              const ts = createdAt as { toDate?: () => Date };
              if (typeof ts.toDate === 'function') {
                createdAtStr = ts.toDate().toISOString();
              } else if (typeof createdAt === 'string') {
                createdAtStr = createdAt;
              }
            }
            setActiveCultureCampaign({
              id: campaignDoc.id,
              companyId: data.companyId as string,
              title: data.title as string,
              type: 'CULTURE',
              status: 'active',
              createdAt: createdAtStr,
            });
          } else {
            setActiveCultureCampaign(null);
          }
        } catch {
          setActiveCultureCampaign(null);
        }
      })
      .catch(() => setActiveCultureCampaign(null))
      .finally(() => setLoadingCultureCampaign(false));
  }, [companyIdOrNull]);

  useEffect(() => {
    loadCultureCampaign();
  }, [loadCultureCampaign]);

  const loadCultureInvites = useCallback(async () => {
    if (!companyIdOrNull) {
      setCultureInvites([]);
      return;
    }
    setLoadingCultureInvites(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'culture_invites'), where('companyId', '==', companyIdOrNull))
      );
      const list = snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        const createdAt = data.createdAt as { toDate?: () => Date } | string | undefined;
        let createdAtStr: string | undefined;
        if (createdAt) {
          if (typeof createdAt === 'object' && typeof createdAt.toDate === 'function') {
            createdAtStr = createdAt.toDate().toISOString();
          } else if (typeof createdAt === 'string') {
            createdAtStr = createdAt;
          }
        }
        return {
          id: d.id,
          campaignId: data.campaignId as string | undefined,
          employeeId: data.employeeId as string | undefined,
          employeeName: data.employeeName as string | undefined,
          email: data.email as string | undefined,
          department: data.department as string | undefined,
          status: data.status as string | undefined,
          createdAt: createdAtStr,
        };
      });
      list.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      setCultureInvites(list);
    } catch (err) {
      console.error('Error cargando invitaciones de cultura:', err);
      setCultureInvites([]);
    } finally {
      setLoadingCultureInvites(false);
    }
  }, [companyIdOrNull]);

  useEffect(() => {
    if (activeTab === 'cultura' && companyIdOrNull) {
      void loadCultureInvites();
    }
  }, [activeTab, companyIdOrNull, loadCultureInvites]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    const active = resultsModalOpen && (currentResults?.length ?? 0) > 0;
    if (active) {
      document.body.classList.add('nom35-report-open');
    } else {
      document.body.classList.remove('nom35-report-open');
    }
    return () => document.body.classList.remove('nom35-report-open');
  }, [resultsModalOpen, currentResults]);

  const resetForm = () => {
    setEditingId(null);
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormPhone('');
    setFormPosition('');
    setFormDepartment('');
    setFormDateOfHire('');
    setFormSex('');
    setFormBirthDate('');
    setFormMaritalStatus('');
    setFormEducationLevel('');
    setFormJobType('');
    setFormContractType('');
  };

  const handleEdit = (employee: Employee) => {
    setEditingId(employee.id);
    setFormFirstName(employee.firstName);
    setFormLastName(employee.lastName);
    setFormEmail(employee.email);
    setFormPhone(employee.phone ?? '');
    setFormPosition(employee.position ?? '');
    setFormDepartment(employee.department ?? '');
    setFormDateOfHire(employee.dateOfHire ? employee.dateOfHire.slice(0, 10) : '');
    setFormSex(employee.sex ?? '');
    setFormBirthDate(employee.birthDate ? employee.birthDate.slice(0, 10) : '');
    setFormMaritalStatus(employee.maritalStatus ?? '');
    setFormEducationLevel(employee.educationLevel ?? '');
    setFormJobType(employee.jobType ?? '');
    setFormContractType(employee.contractType ?? '');
    setAddModalOpen(true);
  };

  const handleSaveEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyIdOrNull) return;
    setFormSubmitting(true);
    try {
      const emailNormalized = formEmail.trim().toLowerCase();
      const rawDate = formDateOfHire?.trim();
      const dateOfHire =
        rawDate && !Number.isNaN(Date.parse(rawDate))
          ? new Date(rawDate).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10);

      const sexNormalized: Employee['sex'] | null =
        formSex === 'Masculino' || formSex === 'Femenino' || formSex === 'Otro' ? formSex : null;

      const payload = {
        firstName: formFirstName.trim(),
        lastName: formLastName.trim(),
        email: emailNormalized,
        phone: formPhone?.trim() || null,
        position: formPosition.trim() || null,
        department: formDepartment.trim() || null,
        dateOfHire,
        sex: sexNormalized,
        birthDate:
          formBirthDate?.trim() && !Number.isNaN(Date.parse(formBirthDate.trim()))
            ? new Date(formBirthDate.trim()).toISOString().slice(0, 10)
            : null,
        maritalStatus: formMaritalStatus.trim() || null,
        educationLevel: formEducationLevel.trim() || null,
        jobType: formJobType.trim() || null,
        contractType: formContractType.trim() || null,
      };

      if (editingId) {
        await updateDoc(doc(db, 'employees', editingId), payload);
      } else {
        await addDoc(collection(db, 'employees'), {
          ...payload,
          companyId: companyIdOrNull,
          isActive: true,
          createdAt: serverTimestamp(),
        });
      }
      setAddModalOpen(false);
      resetForm();
      loadEmployees();
    } catch (err) {
      console.error(editingId ? 'Error al actualizar empleado' : 'Error agregando empleado', err);
      setError(
        err instanceof Error
          ? err.message
          : editingId
            ? 'Error al actualizar empleado'
            : 'Error al agregar empleado'
      );
    } finally {
      setFormSubmitting(false);
      setEditingId(null);
    }
  };

  const handleArchive = async (emp: Employee) => {
    if (!window.confirm(`¿Archivar a ${emp.firstName} ${emp.lastName}?`)) return;
    setArchivingId(emp.id);
    try {
      const now = new Date().toISOString();
      await updateDoc(doc(db, 'employees', emp.id), {
        isActive: false,
        archivedAt: now,
      });
      setEmployees((prev) => prev.filter((x) => x.id !== emp.id));
    } catch (err) {
      console.error('Error al archivar empleado', err);
      setError(err instanceof Error ? err.message : 'Error al archivar');
    } finally {
      setArchivingId(null);
    }
  };

  const handleRestore = async (emp: Employee) => {
    if (!window.confirm(`¿Restaurar a ${emp.firstName} ${emp.lastName}?`)) return;
    setRestoringId(emp.id);
    try {
      await updateDoc(doc(db, 'employees', emp.id), {
        isActive: true,
        archivedAt: null,
      });
      setEmployees((prev) => prev.filter((x) => x.id !== emp.id));
    } catch (err) {
      console.error('Error al restaurar empleado', err);
      setError(err instanceof Error ? err.message : 'Error al restaurar');
    } finally {
      setRestoringId(null);
    }
  };

  const handleOpenExpediente = async (employeeId: string, employeeName: string, employee?: Employee) => {
    if (!companyIdOrNull) return;
    setSelectedEmployeeName(employeeName);
    setSelectedEmployee(employee ?? employees.find((e) => e.id === employeeId) ?? null);
    setLoadingResults(true);
    setCurrentResults(null);
    setResultsError(null);
    setResultsModalOpen(true);
    try {
      const q = query(
        collection(db, 'nom35_assessments'),
        where('companyId', '==', companyIdOrNull),
        where('employeeId', '==', employeeId),
        where('status', '==', 'completed'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          companyId: data.companyId as string,
          campaignId: (data.campaignId as string) ?? 'temp-campaign-id',
          employeeId: data.employeeId as string,
          employeeName: data.employeeName as string,
          guideType: data.guideType as Nom35Assessment['guideType'],
          status: data.status as 'completed',
          createdAt: data.createdAt as string,
          completedAt: data.completedAt as string | undefined,
          answers: data.answers as Record<string, number> | undefined,
          resultScore: data.resultScore as number | undefined,
          resultLevel: data.resultLevel as string | undefined,
          requiresClinicalAttention: data.requiresClinicalAttention as boolean | undefined,
        } as Nom35Assessment;
      });
      setCurrentResults(list);
    } catch (err) {
      console.error('Error cargando resultados:', err);
      setResultsError(err instanceof Error ? err.message : 'Error al cargar resultados');
    } finally {
      setLoadingResults(false);
    }
  };

  const handleViewResults = async (emp: Employee) => {
    await handleOpenExpediente(emp.id, `${emp.firstName} ${emp.lastName}`.trim(), emp);
  };

  const handleSendClimateAssessment = async (employee: Employee) => {
    if (!companyIdOrNull) return;
    if (!activeClimateCampaign) {
      window.alert('Debes iniciar una campaña de clima activa antes de generar enlaces.');
      return;
    }
    const name = `${employee.firstName} ${employee.lastName}`.trim();
    if (!window.confirm(`¿Generar enlace de encuesta de clima para ${name}?`)) return;
    setSendingClimateId(employee.id);
    setError(null);
    try {
      const now = new Date().toISOString();
      const docRef = await addDoc(collection(db, 'climate_assessments'), {
        companyId: companyIdOrNull,
        campaignId: activeClimateCampaign.id,
        employeeId: employee.id,
        employeeName: name,
        status: 'pending',
        createdAt: now,
      });
      const url = `${window.location.origin}/climate/${docRef.id}`;
      await navigator.clipboard.writeText(url);
      setClimateAssessments((prev) => [
        {
          id: docRef.id,
          companyId: companyIdOrNull,
          campaignId: activeClimateCampaign.id,
          employeeId: employee.id,
          employeeName: name,
          status: 'pending',
          createdAt: now,
        },
        ...prev,
      ]);
      setClimateSelectedEmployee(null);
      setClimateEmployeeSearch('');
      window.alert('✅ Enlace copiado al portapapeles.');
    } catch (err) {
      console.error('Error al generar enlace de clima:', err);
      setError(err instanceof Error ? err.message : 'Error al generar enlace');
    } finally {
      setSendingClimateId(null);
    }
  };

  const handleSendClimateToAll = async () => {
    if (!companyIdOrNull || employees.length === 0) return;
    if (!activeClimateCampaign) {
      window.alert('Debes iniciar una campaña de clima activa antes de enviar invitaciones.');
      return;
    }
    const withEmail = employees.filter((e) => e.email?.trim());
    if (withEmail.length === 0) {
      window.alert('No hay empleados con correo electrónico para enviar.');
      return;
    }
    if (employees.length !== withEmail.length) {
      if (!window.confirm(`${employees.length - withEmail.length} empleado(s) sin correo serán omitidos. ¿Enviar invitación por correo a ${withEmail.length}?`)) return;
    } else if (!window.confirm(`Se enviará la invitación de encuesta de clima por correo a ${withEmail.length} empleado(s). ¿Continuar?`)) {
      return;
    }
    setClimateSendAllDialogOpen(false);
    setIsSendingClimateToAll(true);
    setError(null);
    const functions = getFunctions();
    const sendBatch = httpsCallable(functions, 'sendAssessmentEmailBatch');
    try {
      const recipients = withEmail.map((emp) => ({
        toEmail: emp.email!.trim(),
        employeeName: `${emp.firstName} ${emp.lastName}`.trim(),
        employeeId: emp.id,
      }));
      const { data } = await sendBatch({
        recipients,
        testType: 'Clima Organizacional',
        campaignId: activeClimateCampaign.id,
        companyId: companyIdOrNull,
        originUrl: window.location.origin,
      }) as { data: { sentCount: number; failedCount: number; failed: Array<{ employeeName: string; error: string }>; created: Array<{ id: string; companyId: string; campaignId: string; employeeId: string; employeeName: string; status: string; createdAt: string }> } };
      if (data.created?.length) {
        setClimateAssessments((prev) => [...data.created.map((c) => ({ ...c })), ...prev]);
      }
      if (data.failedCount > 0) {
        setError(`${data.failedCount} fallo(s): ${data.failed.slice(0, 3).map((f) => `${f.employeeName}: ${f.error}`).join('; ')}${data.failedCount > 3 ? '...' : ''}`);
        window.alert(`✅ ${data.sentCount} invitaciones enviadas correctamente. ${data.failedCount} fallaron.`);
      } else {
        window.alert(`✅ ${data.sentCount} invitaciones enviadas correctamente`);
      }
    } catch (err) {
      console.error('Error al enviar invitaciones de clima:', err);
      setError(err instanceof Error ? err.message : 'Error al enviar invitaciones');
      window.alert(`❌ ${err instanceof Error ? err.message : 'Error al enviar invitaciones'}`);
    } finally {
      setIsSendingClimateToAll(false);
    }
  };

  const handleGenerateNom35Link = async (employee: Employee) => {
    if (!activeCampaign) {
      window.alert('Debes iniciar una campaña activa antes de generar enlaces.');
      return;
    }
    if (!companyIdOrNull) return;
    const name = `${employee.firstName} ${employee.lastName}`.trim();
    if (!window.confirm(`¿Generar enlace de evaluación NOM-035 para ${name}?`)) return;
    setSendingNom35LinkId(employee.id);
    setError(null);
    try {
      const now = new Date().toISOString();
      const guides = getNom35GuidesToSend(employees.filter((e) => e.isActive).length);
      let firstAssessmentId: string;

      if (guides.length >= 2) {
        const nextGuide = guides[1]!;
        const g1Ref = doc(collection(db, 'nom35_assessments'));
        const nextRef = doc(collection(db, 'nom35_assessments'));
        const batch = writeBatch(db);

        batch.set(nextRef, {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName: name,
          guideType: nextGuide,
          status: 'pending',
          createdAt: now,
        });
        batch.set(g1Ref, {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName: name,
          guideType: 'NOM35_G1',
          status: 'pending',
          createdAt: now,
          nextAssessmentId: nextRef.id,
        });
        await batch.commit();
        firstAssessmentId = g1Ref.id;
      } else {
        const docRef = await addDoc(collection(db, 'nom35_assessments'), {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName: name,
          guideType: 'NOM35_G1',
          status: 'pending',
          createdAt: now,
        });
        firstAssessmentId = docRef.id;
      }

      const url = `${window.location.origin}/assessment/${firstAssessmentId}`;
      await navigator.clipboard.writeText(url);
      setDirectoryNom35Selected(null);
      window.alert('✅ Enlace NOM-035 copiado al portapapeles.');
    } catch (err) {
      console.error('Error al generar enlace NOM-035:', err);
      setError(err instanceof Error ? err.message : 'Error al generar enlace');
    } finally {
      setSendingNom35LinkId(null);
    }
  };

  const handleSendAssessment = async (employee: Employee) => {
    if (!activeCampaign) {
      window.alert('Debes iniciar una campaña activa antes de enviar invitaciones.');
      return;
    }
    if (!window.confirm(`¿Generar enlace de evaluación NOM-035 para ${employee.firstName} ${employee.lastName}?`))
      return;
    if (!companyIdOrNull) return;
    if (!employee.email?.trim()) {
      window.alert('El empleado no tiene correo electrónico registrado.');
      return;
    }
    setSendingAssessmentId(employee.id);
    setError(null);
    try {
      const now = new Date().toISOString();
      const employeeName = `${employee.firstName} ${employee.lastName}`.trim();
      const guides = getNom35GuidesToSend(employees.filter((e) => e.isActive).length);
      let firstAssessmentId: string;

      if (guides.length >= 2) {
        const nextGuide = guides[1]!;
        const g1Ref = doc(collection(db, 'nom35_assessments'));
        const nextRef = doc(collection(db, 'nom35_assessments'));
        const batch = writeBatch(db);

        batch.set(nextRef, {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName,
          guideType: nextGuide,
          status: 'pending',
          createdAt: now,
        });
        batch.set(g1Ref, {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName,
          guideType: 'NOM35_G1',
          status: 'pending',
          createdAt: now,
          nextAssessmentId: nextRef.id,
        });
        await batch.commit();
        firstAssessmentId = g1Ref.id;
      } else {
        const docRef = await addDoc(collection(db, 'nom35_assessments'), {
          companyId: companyIdOrNull,
          campaignId: activeCampaign.id,
          employeeId: employee.id,
          employeeName,
          guideType: 'NOM35_G1',
          status: 'pending',
          createdAt: now,
        });
        firstAssessmentId = docRef.id;
      }

      const assessmentUrl = `${window.location.origin}/assessment/${firstAssessmentId}`;

      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendAssessmentEmail');
      await sendEmail({
        toEmail: employee.email,
        employeeName,
        assessmentUrl,
        testType: 'NOM-035',
      });

      window.alert('✅ Invitación enviada por correo con éxito');
    } catch (err) {
      console.error('Error al enviar invitación por correo', err);
      const msg = err instanceof Error ? err.message : 'Error al enviar invitación por correo';
      setError(msg);
      window.alert(`❌ ${msg}`);
    } finally {
      setSendingAssessmentId(null);
    }
  };

  const handleSendAssessmentToAll = async (mode: 'pending' | 'all') => {
    if (!companyIdOrNull || !activeCampaign) return;
    const targets =
      mode === 'pending'
        ? employees.filter((e) => !completedEmployeeIds.has(e.id))
        : [...employees];
    const withEmail = targets.filter((e) => e.email?.trim());
    if (withEmail.length === 0) {
      window.alert('No hay empleados con correo electrónico para enviar.');
      return;
    }
    if (targets.length !== withEmail.length) {
      if (!window.confirm(`${targets.length - withEmail.length} empleado(s) sin correo serán omitidos. ¿Continuar con ${withEmail.length}?`)) return;
    } else if (!window.confirm(`Se enviará la invitación por correo a ${withEmail.length} empleado(s). ¿Continuar?`)) {
      return;
    }
    setIsSendingToAll(true);
    setSendAllDialogOpen(false);
    setError(null);
    const functions = getFunctions();
    const sendBatch = httpsCallable(functions, 'sendAssessmentEmailBatch');
    try {
      const recipients = withEmail.map((emp) => ({
        toEmail: emp.email!.trim(),
        employeeName: `${emp.firstName} ${emp.lastName}`.trim(),
        employeeId: emp.id,
      }));
      const { data } = await sendBatch({
        recipients,
        testType: 'NOM-035',
        campaignId: activeCampaign.id,
        companyId: companyIdOrNull,
        originUrl: window.location.origin,
      }) as { data: { sentCount: number; failedCount: number; failed: Array<{ employeeName: string; error: string }> } };
      loadEmployees();
      if (data.failedCount > 0) {
        setError(`${data.failedCount} fallo(s): ${data.failed.slice(0, 3).map((f) => `${f.employeeName}: ${f.error}`).join('; ')}${data.failedCount > 3 ? '...' : ''}`);
        window.alert(`✅ ${data.sentCount} invitaciones enviadas correctamente. ${data.failedCount} fallaron.`);
      } else {
        window.alert(`✅ ${data.sentCount} invitaciones enviadas correctamente`);
      }
    } catch (err) {
      console.error('Error al enviar invitaciones', err);
      const msg = err instanceof Error ? err.message : 'Error al enviar invitaciones';
      setError(msg);
      window.alert(`❌ ${msg}`);
    } finally {
      setIsSendingToAll(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyIdOrNull || !newCampaignTitle.trim()) return;
    setCreatingCampaign(true);
    try {
      const docRef = await addDoc(collection(db, 'assessment_campaigns'), {
        companyId: companyIdOrNull,
        title: newCampaignTitle.trim(),
        type: 'NOM35',
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setActiveCampaign({
        id: docRef.id,
        companyId: companyIdOrNull,
        title: newCampaignTitle.trim(),
        type: 'NOM35',
        status: 'active',
        createdAt: new Date().toISOString(),
      });
      setNewCampaignModalOpen(false);
      setNewCampaignTitle('');
      loadEmployees();
    } catch (err) {
      console.error('Error al crear campaña:', err);
      setError(err instanceof Error ? err.message : 'Error al crear campaña');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !companyIdOrNull) return;
    setImportingFile(true);
    setParsingError(null);
    setParsedEmployees([]);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) ?? '';
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length < 2) {
          setParsingError('El archivo debe tener al menos una fila de encabezados y una de datos.');
          setImportingFile(false);
          return;
        }
        const headerLine = lines[0];
        const delimiter = headerLine.includes(';') ? ';' : ',';
        const parseCsvLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"') {
              inQuotes = !inQuotes;
            } else if (c === delimiter && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += c;
            }
          }
          result.push(current.trim());
          return result;
        };
        const headers = parseCsvLine(headerLine).map((h) => h.toLowerCase().trim());
        const findCol = (...names: string[]): number => {
          for (const n of names) {
            const idx = headers.indexOf(n);
            if (idx >= 0) return idx;
          }
          return -1;
        };
        const emailIdx = findCol('email', 'correo');
        if (emailIdx < 0) {
          setParsingError('No se encontró columna de email o correo. Es obligatoria.');
          setImportingFile(false);
          return;
        }
        const nombreIdx = findCol('nombre');
        const apellidoIdx = findCol('apellido');
        const nombreCompletoIdx = findCol('nombre completo', 'nombre_completo');
        const telefonoIdx = findCol('teléfono', 'telefono', 'celular');
        const puestoIdx = findCol('puesto', 'cargo');
        const deptoIdx = findCol('departamento', 'área', 'area');
        const fechaIdx = findCol('fecha', 'ingreso');
        const defaultDate = new Date().toISOString().slice(0, 10);
        const list: ParsedEmployee[] = [];
        for (let i = 1; i < lines.length; i++) {
          const cells = parseCsvLine(lines[i]);
          const email = (cells[emailIdx] ?? '').trim().toLowerCase();
          if (!email) continue;
          let firstName: string;
          let lastName: string;
          if (apellidoIdx >= 0 && nombreIdx >= 0) {
            firstName = (cells[nombreIdx] ?? '').trim();
            lastName = (cells[apellidoIdx] ?? '').trim();
          } else if (nombreIdx >= 0) {
            const parsed = parseFullName(cells[nombreIdx] ?? '');
            firstName = parsed.firstName;
            lastName = parsed.lastName;
          } else if (nombreCompletoIdx >= 0) {
            const parsed = parseFullName(cells[nombreCompletoIdx] ?? '');
            firstName = parsed.firstName;
            lastName = parsed.lastName;
          } else {
            firstName = '';
            lastName = '';
          }
          const phone = telefonoIdx >= 0 ? (cells[telefonoIdx] ?? '').trim() || undefined : undefined;
          const position = puestoIdx >= 0 ? (cells[puestoIdx] ?? '').trim() || undefined : undefined;
          const department = deptoIdx >= 0 ? (cells[deptoIdx] ?? '').trim() || undefined : undefined;
          let dateOfHire = defaultDate;
          if (fechaIdx >= 0 && cells[fechaIdx]) {
            const raw = cells[fechaIdx].trim();
            if (raw) {
              const parsed = Date.parse(raw);
              dateOfHire = !Number.isNaN(parsed) ? new Date(parsed).toISOString().slice(0, 10) : defaultDate;
            }
          }
          list.push({ firstName, lastName, email, phone, position, department, dateOfHire });
        }
        if (list.length === 0) {
          setParsingError('No se encontraron filas válidas con email.');
        } else {
          setParsedEmployees(list);
          setImportModalOpen(true);
        }
      } catch (err) {
        setParsingError(err instanceof Error ? err.message : 'Error al procesar el CSV');
      } finally {
        setImportingFile(false);
      }
    };
    reader.onerror = () => {
      setParsingError('Error al leer el archivo');
      setImportingFile(false);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!companyIdOrNull || parsedEmployees.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      const BATCH_SIZE = 500;
      for (let i = 0; i < parsedEmployees.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = parsedEmployees.slice(i, i + BATCH_SIZE);
        for (const emp of chunk) {
          const ref = doc(collection(db, 'employees'));
          batch.set(ref, {
            companyId: companyIdOrNull,
            firstName: emp.firstName,
            lastName: emp.lastName,
            email: emp.email.trim().toLowerCase(),
            phone: emp.phone || null,
            position: emp.position || null,
            department: emp.department || null,
            dateOfHire: emp.dateOfHire,
            isActive: true,
            createdAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }
      setImportModalOpen(false);
      setParsedEmployees([]);
      setParsingError(null);
      loadEmployees();
    } catch (err) {
      console.error('Error al importar empleados:', err);
      setError(err instanceof Error ? err.message : 'Error al importar empleados');
    } finally {
      setImporting(false);
    }
  };

  const handleCloseCampaign = async () => {
    if (!activeCampaign) return;
    if (
      !window.confirm(
        '¿Estás seguro de cerrar esta campaña? Ya no se podrán enviar nuevas invitaciones para este ciclo.'
      )
    ) {
      return;
    }
    setClosingCampaignId(activeCampaign.id);
    setError(null);
    try {
      await updateDoc(doc(db, 'assessment_campaigns', activeCampaign.id), {
        status: 'closed',
        closedAt: serverTimestamp(),
      });
      setActiveCampaign(null);
      loadEmployees();
    } catch (err) {
      console.error('Error al cerrar campaña:', err);
      setError(err instanceof Error ? err.message : 'Error al cerrar campaña');
    } finally {
      setClosingCampaignId(null);
    }
  };

  const loadClimateHistory = useCallback(async () => {
    if (!companyIdOrNull) {
      setClosedClimateCampaigns([]);
      return;
    }
    setLoadingClimateHistory(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'assessment_campaigns'),
          where('companyId', '==', companyIdOrNull),
          where('type', '==', 'CLIMATE'),
          where('status', '==', 'closed'),
          orderBy('createdAt', 'desc')
        )
      );
      const list = snap.docs.map((d) => {
        const data = d.data();
        const createdAt = data.createdAt;
        let createdAtStr = new Date().toISOString();
        if (createdAt) {
          const ts = createdAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
          else if (typeof createdAt === 'string') createdAtStr = createdAt;
        }
        const closedAt = data.closedAt;
        let closedAtStr: string | undefined;
        if (closedAt) {
          const ts = closedAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') closedAtStr = ts.toDate().toISOString();
          else if (typeof closedAt === 'string') closedAtStr = closedAt;
        }
        return {
          id: d.id,
          companyId: data.companyId as string,
          title: data.title as string,
          type: 'CLIMATE' as const,
          status: 'closed' as const,
          createdAt: createdAtStr,
          closedAt: closedAtStr,
        } as AssessmentCampaign;
      });
      setClosedClimateCampaigns(list);
    } catch (err) {
      console.error('Error cargando historial de clima:', err);
      setClosedClimateCampaigns([]);
    } finally {
      setLoadingClimateHistory(false);
    }
  }, [companyIdOrNull]);

  const handleCreateClimateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyIdOrNull || !newClimateCampaignTitle.trim()) return;
    setCreatingClimateCampaign(true);
    try {
      const docRef = await addDoc(collection(db, 'assessment_campaigns'), {
        companyId: companyIdOrNull,
        title: newClimateCampaignTitle.trim(),
        type: 'CLIMATE',
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setActiveClimateCampaign({
        id: docRef.id,
        companyId: companyIdOrNull,
        title: newClimateCampaignTitle.trim(),
        type: 'CLIMATE',
        status: 'active',
        createdAt: new Date().toISOString(),
      });
      setNewClimateCampaignModalOpen(false);
      setNewClimateCampaignTitle('');
    } catch (err) {
      console.error('Error al crear campaña de clima:', err);
      setError(err instanceof Error ? err.message : 'Error al crear campaña');
    } finally {
      setCreatingClimateCampaign(false);
    }
  };

  const handleCloseClimateCampaign = async () => {
    if (!activeClimateCampaign) return;
    if (!window.confirm('¿Estás seguro de cerrar esta campaña de clima? Ya no se podrán generar nuevos enlaces para este ciclo.')) return;
    setClosingClimateId(activeClimateCampaign.id);
    setError(null);
    try {
      await updateDoc(doc(db, 'assessment_campaigns', activeClimateCampaign.id), {
        status: 'closed',
        closedAt: serverTimestamp(),
      });
      setActiveClimateCampaign(null);
    } catch (err) {
      console.error('Error al cerrar campaña de clima:', err);
      setError(err instanceof Error ? err.message : 'Error al cerrar campaña');
    } finally {
      setClosingClimateId(null);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!companyIdOrNull) {
      setClosedCampaigns([]);
      return;
    }
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'assessment_campaigns'),
          where('companyId', '==', companyIdOrNull),
          where('type', '==', 'NOM35'),
          where('status', '==', 'closed'),
          orderBy('createdAt', 'desc')
        )
      );
      const list = snap.docs.map((d) => {
        const data = d.data();
        const createdAt = data.createdAt;
        let createdAtStr = new Date().toISOString();
        if (createdAt) {
          const ts = createdAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
          else if (typeof createdAt === 'string') createdAtStr = createdAt;
        }
        const closedAt = data.closedAt;
        let closedAtStr: string | undefined;
        if (closedAt) {
          const ts = closedAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') closedAtStr = ts.toDate().toISOString();
          else if (typeof closedAt === 'string') closedAtStr = closedAt;
        }
        return {
          id: d.id,
          companyId: data.companyId as string,
          title: data.title as string,
          type: 'NOM35' as const,
          status: 'closed' as const,
          createdAt: createdAtStr,
          closedAt: closedAtStr,
        } as AssessmentCampaign;
      });
      setClosedCampaigns(list);
    } catch (err) {
      console.error('Error cargando historial de campañas:', err);
      setHistoryError(err instanceof Error ? err.message : 'Error al cargar historial');
      setClosedCampaigns([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [companyIdOrNull]);

  useEffect(() => {
    if (historyModalOpen) {
      loadHistory();
    }
  }, [historyModalOpen, loadHistory]);

  useEffect(() => {
    if (climateHistoryModalOpen) {
      loadClimateHistory();
    }
  }, [climateHistoryModalOpen, loadClimateHistory]);

  const loadClosedCultureCampaigns = useCallback(async () => {
    if (!companyIdOrNull) {
      setClosedCultureCampaigns([]);
      return;
    }
    setLoadingCultureHistory(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'assessment_campaigns'),
          where('companyId', '==', companyIdOrNull),
          where('type', '==', 'CULTURE'),
          where('status', '==', 'closed'),
          orderBy('createdAt', 'desc')
        )
      );
      const list = snap.docs.map((d) => {
        const data = d.data();
        const createdAt = data.createdAt;
        let createdAtStr = new Date().toISOString();
        if (createdAt) {
          const ts = createdAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') createdAtStr = ts.toDate().toISOString();
          else if (typeof createdAt === 'string') createdAtStr = createdAt;
        }
        const closedAt = data.closedAt;
        let closedAtStr: string | undefined;
        if (closedAt) {
          const ts = closedAt as { toDate?: () => Date };
          if (typeof ts.toDate === 'function') closedAtStr = ts.toDate().toISOString();
          else if (typeof closedAt === 'string') closedAtStr = closedAt;
        }
        return {
          id: d.id,
          companyId: data.companyId as string,
          title: data.title as string,
          type: 'CULTURE' as const,
          status: 'closed' as const,
          createdAt: createdAtStr,
          closedAt: closedAtStr,
        } as AssessmentCampaign;
      });
      setClosedCultureCampaigns(list);
    } catch (err) {
      console.error('Error cargando historial de cultura:', err);
      setClosedCultureCampaigns([]);
    } finally {
      setLoadingCultureHistory(false);
    }
  }, [companyIdOrNull]);

  const handleCreateCultureCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyIdOrNull || !newCultureCampaignTitle.trim()) return;
    setCreatingCultureCampaign(true);
    try {
      const docRef = await addDoc(collection(db, 'assessment_campaigns'), {
        companyId: companyIdOrNull,
        title: newCultureCampaignTitle.trim(),
        type: 'CULTURE',
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setActiveCultureCampaign({
        id: docRef.id,
        companyId: companyIdOrNull,
        title: newCultureCampaignTitle.trim(),
        type: 'CULTURE',
        status: 'active',
        createdAt: new Date().toISOString(),
      });
      setNewCultureCampaignModalOpen(false);
      setNewCultureCampaignTitle('');
    } catch (err) {
      console.error('Error al crear campaña de cultura:', err);
      setError(err instanceof Error ? err.message : 'Error al crear campaña');
    } finally {
      setCreatingCultureCampaign(false);
    }
  };

  const handleCloseCultureCampaign = async () => {
    if (!activeCultureCampaign) return;
    if (
      !window.confirm(
        '¿Estás seguro de cerrar esta campaña de cultura? Ya no se podrán enviar nuevas invitaciones para este ciclo.'
      )
    ) {
      return;
    }
    setClosingCultureCampaignId(activeCultureCampaign.id);
    setError(null);
    try {
      await updateDoc(doc(db, 'assessment_campaigns', activeCultureCampaign.id), {
        status: 'closed',
        closedAt: serverTimestamp(),
      });
      setActiveCultureCampaign(null);
      await loadCultureInvites();
    } catch (err) {
      console.error('Error al cerrar campaña de cultura:', err);
      setError(err instanceof Error ? err.message : 'Error al cerrar campaña');
    } finally {
      setClosingCultureCampaignId(null);
    }
  };

  useEffect(() => {
    if (cultureHistoryModalOpen) {
      void loadClosedCultureCampaigns();
    }
  }, [cultureHistoryModalOpen, loadClosedCultureCampaigns]);

  const handleOpenSendAllDialog = () => {
    if (!activeCampaign) {
      window.alert('Debes iniciar una campaña activa antes de enviar invitaciones.');
      return;
    }
    if (employees.length === 0) {
      window.alert('No hay empleados en el directorio. Agrega empleados primero.');
      return;
    }
    if (!hasAnyCompleted && pendingCount === employees.length) {
      handleSendAssessmentToAll('all');
      return;
    }
    if (pendingCount === 0) {
      setSendAllDialogOpen(true);
      return;
    }
    setSendAllDialogOpen(true);
  };

  const handleHardDelete = async (emp: Employee) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${emp.firstName} ${emp.lastName}?`)) return;
    setHardDeletingId(emp.id);
    try {
      await deleteDoc(doc(db, 'employees', emp.id));
      setEmployees((prev) => prev.filter((x) => x.id !== emp.id));
    } catch (err) {
      console.error('Error al eliminar empleado', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar definitivamente');
    } finally {
      setHardDeletingId(null);
    }
  };

  const handleDownloadGlobalPdf = async () => {
    if (!companyIdOrNull) return;
    setIsGeneratingGlobalPdf(true);
    try {
      const companySnap = await getDoc(doc(db, 'companies', companyIdOrNull));
      const logoUrl = companySnap.exists() ? (companySnap.data()?.branding as { logoUrl?: string } | undefined)?.logoUrl : null;
      await generateNom35GlobalPdf(latestAssessments, logoUrl);
    } catch (err) {
      console.error('Error generando PDF global:', err);
      window.alert(err instanceof Error ? err.message : 'Error al generar el PDF. Permite ventanas emergentes.');
    } finally {
      setIsGeneratingGlobalPdf(false);
    }
  };

  const handleDownloadExpedientePdf = async () => {
    if (!currentResults || currentResults.length === 0 || !companyIdOrNull) return;
    const emp = selectedEmployee ?? {
      id: currentResults[0].employeeId,
      companyId: currentResults[0].companyId,
      firstName: currentResults[0].employeeName.split(' ')[0] ?? '',
      lastName: currentResults[0].employeeName.split(' ').slice(1).join(' ') ?? '',
      email: '',
      dateOfHire: '',
      isActive: true,
    } as Employee;
    setIsGeneratingExpedientePdf(true);
    try {
      const companySnap = await getDoc(doc(db, 'companies', companyIdOrNull));
      const logoUrl = companySnap.exists() ? (companySnap.data()?.branding as { logoUrl?: string } | undefined)?.logoUrl : null;
      await generateNom35ExpedientePdf(emp, currentResults, logoUrl);
    } catch (err) {
      console.error('Error generando expediente PDF:', err);
      window.alert(err instanceof Error ? err.message : 'Error al generar el PDF. Permite ventanas emergentes.');
    } finally {
      setIsGeneratingExpedientePdf(false);
    }
  };

  if (!appUser?.companyId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-muted-foreground">Acceso Restringido: No tienes empresa asignada</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Desarrollo Organizacional</h1>
        <p className="text-muted-foreground">
          Directorio, bitácora, NOM-035 y clima laboral.
        </p>
      </div>

      {companyCountry !== 'MX' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <strong>Nota:</strong> La NOM-035 es una normativa exclusiva para centros de trabajo en territorio mexicano. Los resultados aquí mostrados cumplen con los criterios legales de México (STPS).
        </div>
      )}

      <div className="flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('directorio')}
          className={cn(
            'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'directorio'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Directorio
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('bitacora')}
          className={cn(
            'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'bitacora'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Bitácora
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('evaluaciones')}
          className={cn(
            'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'evaluaciones'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Reportes NOM 035
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('clima')}
          className={cn(
            'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'clima'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Clima Organizacional
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('cultura')}
          className={cn(
            'flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'cultura'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Users className="h-4 w-4" />
          Cultura Organizacional
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('papelera')}
          className={cn(
            'flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'papelera'
              ? 'border-amber-600 text-amber-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Trash2 className="h-4 w-4" />
          Papelera
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={loadEmployees}>
            Reintentar
          </Button>
        </div>
      )}

      {activeTab === 'directorio' && (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 flex-col gap-2 sm:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar empleados..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setDirectoryNom35DropdownOpen(true);
                  }}
                  onFocus={() => setDirectoryNom35DropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDirectoryNom35DropdownOpen(false), 150)}
                  className="pl-9"
                />
                {directoryNom35DropdownOpen && filteredEmployees.length > 0 && (
                  <div
                    className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border bg-card shadow-lg"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {filteredEmployees.map((emp) => (
                      <button
                        key={emp.id}
                        type="button"
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                        onMouseDown={() => {
                          setDirectoryNom35Selected(emp);
                          setDirectoryNom35DropdownOpen(false);
                        }}
                      >
                        <span>
                          <span className="font-medium">{emp.firstName} {emp.lastName}</span>
                          {emp.department && (
                            <span className="ml-1 text-xs text-muted-foreground">({emp.department})</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {directoryNom35Selected && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Generar enlace NOM-035 para: <strong>{directoryNom35Selected.firstName} {directoryNom35Selected.lastName}</strong>
                  </span>
                  <Button
                    size="sm"
                    disabled={sendingNom35LinkId !== null || !activeCampaign}
                    onClick={() => handleGenerateNom35Link(directoryNom35Selected)}
                  >
                    {sendingNom35LinkId === directoryNom35Selected.id ? 'Generando…' : 'Generar enlace NOM-035'}
                  </Button>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setDirectoryNom35Selected(null)}
                  >
                    Limpiar
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleOpenSendAllDialog}
                disabled={!activeCampaign || employees.length === 0 || isSendingToAll}
              >
                <Mail className="mr-2 h-4 w-4" />
                {isSendingToAll ? 'Enviando…' : 'Enviar a todos'}
              </Button>
              <Button variant="outline" onClick={() => setImportModalOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Importar CSV
              </Button>
              <Button onClick={() => setAddModalOpen(true)}>Agregar empleado</Button>
            </div>
          </div>
          {loading ? (
            <p className="text-muted-foreground">Cargando...</p>
          ) : employees.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">No hay empleados registrados.</p>
                <Button className="mt-4" onClick={() => setAddModalOpen(true)}>
                  Agregar primer empleado
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Directorio de empleados</CardTitle>
                <CardDescription>Personal activo a evaluar según NOM-035</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 pr-4 font-medium">Nombre</th>
                        <th className="pb-2 pr-4 font-medium">Email</th>
                        <th className="pb-2 pr-4 font-medium">Teléfono</th>
                        <th className="pb-2 pr-4 font-medium">Puesto</th>
                        <th className="pb-2 pr-4 font-medium">Departamento</th>
                        <th className="pb-2 pr-4 font-medium">Estado NOM-035</th>
                        <th className="pb-2 pr-4 font-medium">Nivel de Riesgo</th>
                        <th className="pb-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-8 text-center text-muted-foreground">
                            {searchQuery.trim() ? 'No hay empleados que coincidan con la búsqueda.' : 'No hay empleados.'}
                          </td>
                        </tr>
                      ) : (
                      filteredEmployees.map((emp) => {
                        const riskLevel = getEmployeeRiskLevel(emp);
                        return (
                          <tr key={emp.id} className="border-b last:border-0">
                            <td className="py-3 pr-4">
                              {emp.firstName} {emp.lastName}
                            </td>
                            <td className="py-3 pr-4">{emp.email}</td>
                            <td className="py-3 pr-4">
                              {emp.phone ? (
                                <a
                                  href={`https://wa.me/${emp.phone.replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"
                                >
                                  <MessageCircle className="h-4 w-4 shrink-0" />
                                  {formatPhoneNumberIntl(emp.phone) || emp.phone}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">Sin teléfono</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">{emp.position ?? '—'}</td>
                            <td className="py-3 pr-4">{emp.department ?? '—'}</td>
                            <td className="py-3 pr-4">
                              <span
                                className={cn(
                                  'inline-block rounded-full px-2 py-0.5 text-xs',
                                  completedEmployeeIds.has(emp.id) && 'bg-green-100 text-green-800',
                                  !completedEmployeeIds.has(emp.id) && 'bg-yellow-100 text-yellow-800'
                                )}
                              >
                                {completedEmployeeIds.has(emp.id) ? 'Completado' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  setHistoryEmployee({ id: emp.id, name: `${emp.firstName} ${emp.lastName}`.trim() });
                                  setHistoryDialogOpen(true);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setHistoryEmployee({ id: emp.id, name: `${emp.firstName} ${emp.lastName}`.trim() });
                                    setHistoryDialogOpen(true);
                                  }
                                }}
                                className={cn(
                                  'cursor-pointer transition-colors',
                                  riskLevel === 'sin_riesgo' && 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200',
                                  riskLevel === 'requiere_atencion' && 'bg-red-100 text-red-700 hover:bg-red-200 border-red-200',
                                  riskLevel === 'sin_datos' && 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:border-slate-700'
                                )}
                              >
                                {riskLevel === 'sin_riesgo' && 'Sin Riesgo'}
                                {riskLevel === 'requiere_atencion' && 'Requiere Atención'}
                                {riskLevel === 'sin_datos' && 'Sin Datos'}
                              </Badge>
                            </td>
                            <td className="py-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger aria-label="Acciones">
                                  <MoreVertical className="h-4 w-4 text-muted-foreground" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEdit(emp)}>
                                    ✏️ Editar Empleado
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    destructive
                                    onClick={() => archivingId !== emp.id && handleArchive(emp)}
                                    className={archivingId === emp.id ? 'cursor-not-allowed opacity-50' : ''}
                                  >
                                    🗑️ Archivar
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog open={newCampaignModalOpen} onOpenChange={setNewCampaignModalOpen}>
            <DialogContent>
              <form onSubmit={handleCreateCampaign}>
                <DialogHeader>
                  <DialogTitle>Iniciar Nueva Campaña</DialogTitle>
                  <DialogDescription>
                    Crea una campaña de evaluación NOM-035 para el ciclo actual. Solo puede haber una campaña activa a la vez.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="campaignTitle">Nombre de la Campaña</Label>
                    <Input
                      id="campaignTitle"
                      placeholder="Ej. Evaluación Anual 2026"
                      value={newCampaignTitle}
                      onChange={(e) => setNewCampaignTitle(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNewCampaignModalOpen(false);
                      setNewCampaignTitle('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creatingCampaign || !newCampaignTitle.trim()}>
                    {creatingCampaign ? 'Creando…' : 'Crear Campaña'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog
            open={importModalOpen}
            onOpenChange={(open) => {
              setImportModalOpen(open);
              if (!open) {
                setParsedEmployees([]);
                setParsingError(null);
              }
            }}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Importar empleados desde CSV</DialogTitle>
                <DialogDescription>
                  Sube un archivo CSV con columnas: email/correo (obligatorio), nombre, apellido (o nombre completo), teléfono, puesto, departamento, fecha de ingreso.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                {importingFile ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Procesando archivo...</p>
                ) : parsingError ? (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                    <p>{parsingError}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setParsingError(null);
                        setParsedEmployees([]);
                      }}
                    >
                      Intentar de nuevo
                    </Button>
                  </div>
                ) : parsedEmployees.length > 0 ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      Revisa los datos. Si alguna columna no se detectó bien, puedes cancelar, corregir tu Excel y volver a subirlo.
                    </p>
                    <div className="overflow-x-auto rounded-md border max-h-[400px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted/95">
                          <tr className="border-b">
                            <th className="px-4 py-2.5 text-left font-medium">Nombre</th>
                            <th className="px-4 py-2.5 text-left font-medium">Apellido</th>
                            <th className="px-4 py-2.5 text-left font-medium">Email</th>
                            <th className="px-4 py-2.5 text-left font-medium">Teléfono</th>
                            <th className="px-4 py-2.5 text-left font-medium">Puesto</th>
                            <th className="px-4 py-2.5 text-left font-medium">Departamento</th>
                            <th className="px-4 py-2.5 text-left font-medium">Fecha ingreso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedEmployees.map((emp, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="px-4 py-2.5">{emp.firstName}</td>
                              <td className="px-4 py-2.5">{emp.lastName}</td>
                              <td className="px-4 py-2.5">{emp.email}</td>
                              <td className="px-4 py-2.5">{emp.phone ?? '—'}</td>
                              <td className="px-4 py-2.5">{emp.position ?? '—'}</td>
                              <td className="px-4 py-2.5">{emp.department ?? '—'}</td>
                              <td className="px-4 py-2.5">{emp.dateOfHire}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {parsedEmployees.length} empleado(s) listos para importar.
                    </p>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Label htmlFor="csv-file" className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 px-8 py-12 hover:border-primary/50 hover:bg-muted/50 transition-colors">
                        <Upload className="h-12 w-12 text-muted-foreground" />
                        <span className="text-sm font-medium">Selecciona un archivo CSV</span>
                        <span className="text-xs text-muted-foreground">.csv</span>
                      </div>
                      <input
                        id="csv-file"
                        type="file"
                        accept=".csv"
                        className="sr-only"
                        onChange={handleFileSelect}
                      />
                    </Label>
                  </div>
                )}
              </div>
              {parsedEmployees.length > 0 && (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportModalOpen(false);
                      setParsedEmployees([]);
                      setParsingError(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleConfirmImport} disabled={importing}>
                    {importing ? 'Guardando…' : 'Confirmar y Guardar'}
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={sendAllDialogOpen} onOpenChange={setSendAllDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Enviar invitación NOM-035</DialogTitle>
                <DialogDescription>
                  {pendingCount > 0
                    ? `Hay ${pendingCount} empleado(s) pendientes de recibir la invitación. Elige la acción:`
                    : 'Todos han recibido o completado la evaluación. Puedes iniciar un nuevo ciclo semestral/anual.'}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2 py-4">
                {pendingCount > 0 && (
                  <Button
                    onClick={() => handleSendAssessmentToAll('pending')}
                    disabled={isSendingToAll}
                  >
                    Solo pendientes ({pendingCount})
                  </Button>
                )}
                <Button
                  variant={pendingCount > 0 ? 'outline' : 'default'}
                  onClick={() => handleSendAssessmentToAll('all')}
                  disabled={isSendingToAll}
                >
                  Todos ({employees.length}) — Nueva evaluación
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSendAllDialogOpen(false)}>
                  Cancelar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {activeTab === 'clima' && (
        <div className="space-y-6">
          {/* Banner Campaña Clima */}
          {loadingClimateCampaign ? (
            <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
              Cargando campaña...
            </div>
          ) : !activeClimateCampaign ? (
            <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
              <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  No hay ninguna campaña de clima activa. Inicia una para generar enlaces.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setClimateHistoryModalOpen(true)}>
                    <History className="mr-2 h-4 w-4" />
                    Ver Historial
                  </Button>
                  <Button onClick={() => setNewClimateCampaignModalOpen(true)}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Iniciar Nueva Campaña
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/30 dark:text-green-200">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                Campaña Activa: <strong>{activeClimateCampaign.title}</strong>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClimateHistoryModalOpen(true)}
                  className="border-green-300 text-green-800 hover:bg-green-100 dark:border-green-700 dark:text-green-200 dark:hover:bg-green-900/50"
                >
                  <History className="mr-2 h-4 w-4" />
                  Ver Historial
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseClimateCampaign}
                  disabled={closingClimateId === activeClimateCampaign.id}
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/50"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  {closingClimateId === activeClimateCampaign.id ? 'Cerrando…' : 'Cerrar Campaña'}
                </Button>
              </div>
            </div>
          )}

          {/* Card 1: Invitar a responder */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="text-base">Encuesta de Clima Organizacional</CardTitle>
                  <CardDescription>
                    Genera enlaces vinculados a cada empleado para que respondan la encuesta. Las respuestas se asocian al área/departamento de la empresa.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="shrink-0" asChild>
                    <Link to={activeClimateCampaign ? `/app/climate/results?campaignId=${activeClimateCampaign.id}` : '/app/climate/results'}>
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Ver Resultados
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={loading}
                    onClick={loadEmployees}
                    title="Actualizar lista"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    className="shrink-0"
                    disabled={!activeClimateCampaign || employees.length === 0 || isSendingClimateToAll}
                    onClick={() => setClimateSendAllDialogOpen(true)}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    {isSendingClimateToAll ? 'Enviando…' : `Invitar a todos (${employees.length})`}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Enviar a empleado específico</h3>
                <p className="text-sm text-muted-foreground">
                  Genera un enlace personalizado para seguimiento (por ejemplo, si alguien no respondió o hay contrataciones nuevas).
                </p>
                {employees.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No hay empleados en el directorio. Agrega empleados en la pestaña Directorio.
                  </p>
                ) : (
                  <div className="relative max-w-md">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Buscar por nombre, email o departamento..."
                          value={climateSelectedEmployee ? `${climateSelectedEmployee.firstName} ${climateSelectedEmployee.lastName}` : climateEmployeeSearch}
                          onChange={(e) => {
                            setClimateEmployeeSearch(e.target.value);
                            setClimateSelectedEmployee(null);
                            setClimateDropdownOpen(true);
                          }}
                          onFocus={() => setClimateDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setClimateDropdownOpen(false), 150)}
                          className="pl-9"
                        />
                        {climateDropdownOpen && climateFilteredEmployees.length > 0 && (
                          <div
                            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border bg-card shadow-lg"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {climateFilteredEmployees.map((emp) => (
                              <button
                                key={emp.id}
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                                onMouseDown={() => {
                                  setClimateSelectedEmployee(emp);
                                  setClimateEmployeeSearch('');
                                  setClimateDropdownOpen(false);
                                }}
                              >
                                <span className="font-medium">{emp.firstName} {emp.lastName}</span>
                                {emp.department && (
                                  <span className="text-xs text-muted-foreground">({emp.department})</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        disabled={!activeClimateCampaign || !climateSelectedEmployee || sendingClimateId !== null}
                        onClick={() => climateSelectedEmployee && handleSendClimateAssessment(climateSelectedEmployee)}
                      >
                        {climateSelectedEmployee && sendingClimateId === climateSelectedEmployee.id
                          ? 'Generando…'
                          : 'Generar enlace'}
                      </Button>
                    </div>
                    {climateSelectedEmployee && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        Seleccionado: {climateSelectedEmployee.firstName} {climateSelectedEmployee.lastName}
                        <button
                          type="button"
                          className="ml-2 text-primary hover:underline"
                          onClick={() => {
                            setClimateSelectedEmployee(null);
                            setClimateEmployeeSearch('');
                          }}
                        >
                          Limpiar
                        </button>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Encuestas generadas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Encuestas generadas</CardTitle>
              <CardDescription>
                Enlaces creados. Copia cada uno para enviarlo al empleado correspondiente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2.5 text-left font-medium">Empleado</th>
                      <th className="px-4 py-2.5 text-left font-medium">Fecha</th>
                      <th className="px-4 py-2.5 text-left font-medium">Estado</th>
                      <th className="px-4 py-2.5 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {climateAssessmentsForDisplay.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">
                          <p className="mb-2">No hay encuestas generadas.</p>
                          <p className="text-xs">Usa &quot;Invitar a todos&quot; o genera un enlace para un empleado específico.</p>
                        </td>
                      </tr>
                        ) : (
                          climateAssessmentsForDisplay.map((a) => (
                        <tr key={a.id} className="border-b last:border-0">
                          <td className="px-4 py-2.5 font-medium">
                            {a.employeeName ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {a.createdAt
                              ? new Date(a.createdAt).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={cn(
                                'inline-block rounded-full px-2 py-0.5 text-xs',
                                a.status === 'completed' && 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                                a.status === 'pending' && 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                              )}
                            >
                              {a.status === 'completed' ? 'Completada' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const url = `${window.location.origin}/climate/${a.id}`;
                                navigator.clipboard.writeText(url);
                                window.alert('Enlace copiado al portapapeles.');
                              }}
                            >
                              <Copy className="mr-1.5 h-3.5 w-3.5" />
                              Copiar enlace
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Dialog Invitar a todos */}
          <Dialog open={climateSendAllDialogOpen} onOpenChange={setClimateSendAllDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invitar a todos a la encuesta de clima</DialogTitle>
                <DialogDescription>
                  Se enviará por correo electrónico un enlace de encuesta a cada empleado del directorio que tenga email ({employees.filter((e) => e.email?.trim()).length} de {employees.length}).
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setClimateSendAllDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSendClimateToAll} disabled={isSendingClimateToAll}>
                  {isSendingClimateToAll ? 'Enviando…' : `Enviar por correo (${employees.filter((e) => e.email?.trim()).length})`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={newClimateCampaignModalOpen} onOpenChange={setNewClimateCampaignModalOpen}>
            <DialogContent>
              <form onSubmit={handleCreateClimateCampaign}>
                <DialogHeader>
                  <DialogTitle>Nueva campaña de clima</DialogTitle>
                  <DialogDescription>
                    Crea una campaña para agrupar las encuestas de clima de este periodo (ej. &quot;Clima Q1 2026&quot;).
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="climate-campaign-title">Nombre de la campaña</Label>
                  <Input
                    id="climate-campaign-title"
                    value={newClimateCampaignTitle}
                    onChange={(e) => setNewClimateCampaignTitle(e.target.value)}
                    placeholder="Ej. Clima febrero 2026"
                    className="mt-2"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setNewClimateCampaignModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={!newClimateCampaignTitle.trim() || creatingClimateCampaign}>
                    {creatingClimateCampaign ? 'Creando…' : 'Crear campaña'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={climateHistoryModalOpen} onOpenChange={setClimateHistoryModalOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Historial de campañas de clima</DialogTitle>
                <DialogDescription>
                  Campañas de clima cerradas. Accede a los resultados de cada una.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                {loadingClimateHistory ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Cargando historial...</p>
                ) : closedClimateCampaigns.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No hay campañas de clima anteriores.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-2.5 text-left font-medium">Campaña</th>
                          <th className="px-4 py-2.5 text-left font-medium">Fecha de inicio</th>
                          <th className="px-4 py-2.5 text-left font-medium">Fecha de cierre</th>
                          <th className="px-4 py-2.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {closedClimateCampaigns.map((c) => (
                          <tr key={c.id} className="border-b last:border-0">
                            <td className="px-4 py-2.5 font-medium">{c.title}</td>
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {c.createdAt
                                ? new Date(c.createdAt).toLocaleDateString('es-MX', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {c.closedAt
                                ? new Date(c.closedAt).toLocaleDateString('es-MX', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : '—'}
                            </td>
                            <td className="px-4 py-2.5">
                              <Button size="sm" variant="outline" asChild>
                                <Link to={`/app/climate/results?campaignId=${c.id}`} onClick={() => setClimateHistoryModalOpen(false)}>
                                  Ver Resultados
                                </Link>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === 'cultura' && (
        <div className="space-y-6">
          {loadingCultureCampaign ? (
            <p className="text-sm text-muted-foreground">Cargando campaña de cultura...</p>
          ) : !activeCultureCampaign ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Campaña de Cultura Organizacional</CardTitle>
                <CardDescription>
                  Crea una campaña activa para agrupar invitaciones y resultados por periodo (como en Clima).
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setCultureHistoryModalOpen(true)}>
                  <History className="mr-2 h-4 w-4" />
                  Historial de campañas
                </Button>
                <Button onClick={() => setNewCultureCampaignModalOpen(true)}>Nueva campaña</Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                <p className="text-sm">
                  Campaña activa: <strong>{activeCultureCampaign.title}</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to={`/app/culture?campaignId=${activeCultureCampaign.id}`}
                      onClick={() => loadCultureInvites()}
                    >
                      Resultados e invitaciones
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCultureHistoryModalOpen(true)}>
                    <History className="mr-2 h-4 w-4" />
                    Historial
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => void handleCloseCultureCampaign()}
                    disabled={closingCultureCampaignId === activeCultureCampaign.id}
                  >
                    {closingCultureCampaignId === activeCultureCampaign.id ? 'Cerrando…' : 'Cerrar campaña'}
                  </Button>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Invitaciones de esta campaña</CardTitle>
                  <CardDescription>
                    Envía correos desde la vista de resultados. Aquí ves quién ya respondió y quién está pendiente.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      Completadas:{' '}
                      <strong>{cultureInvitesForDisplay.filter((i) => i.status === 'completed').length}</strong>
                      {' · '}
                      Pendientes:{' '}
                      <strong>{cultureInvitesForDisplay.filter((i) => i.status !== 'completed').length}</strong>
                      {' · '}
                      Total invitaciones: <strong>{cultureInvitesForDisplay.length}</strong>
                    </span>
                    <Button variant="outline" size="sm" onClick={() => void loadCultureInvites()} disabled={loadingCultureInvites}>
                      <RefreshCw className={cn('mr-2 h-4 w-4', loadingCultureInvites && 'animate-spin')} />
                      Actualizar
                    </Button>
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-2.5 text-left font-medium">Colaborador</th>
                          <th className="px-4 py-2.5 text-left font-medium">Correo</th>
                          <th className="px-4 py-2.5 text-left font-medium">Departamento</th>
                          <th className="px-4 py-2.5 text-left font-medium">Fecha invitación</th>
                          <th className="px-4 py-2.5 text-left font-medium">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingCultureInvites ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                              Cargando invitaciones...
                            </td>
                          </tr>
                        ) : cultureInvitesForDisplay.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                              <p className="mb-2">Aún no hay invitaciones para esta campaña.</p>
                              <Button size="sm" variant="outline" asChild>
                                <Link to={`/app/culture?campaignId=${activeCultureCampaign.id}`}>Ir a enviar invitaciones</Link>
                              </Button>
                            </td>
                          </tr>
                        ) : (
                          cultureInvitesForDisplay.map((inv) => {
                            const emp = inv.employeeId ? employees.find((e) => e.id === inv.employeeId) : undefined;
                            const displayName =
                              inv.employeeName?.trim() ||
                              (emp ? `${emp.firstName} ${emp.lastName}`.trim() : '—');
                            return (
                              <tr key={inv.id} className="border-b last:border-0">
                                <td className="px-4 py-2.5 font-medium">{displayName}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">{inv.email ?? '—'}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">{inv.department ?? '—'}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">
                                  {inv.createdAt
                                    ? new Date(inv.createdAt).toLocaleDateString('es-MX', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                      })
                                    : '—'}
                                </td>
                                <td className="px-4 py-2.5">
                                  <span
                                    className={cn(
                                      'inline-block rounded-full px-2 py-0.5 text-xs',
                                      inv.status === 'completed' &&
                                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                                      inv.status !== 'completed' &&
                                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    )}
                                  >
                                    {inv.status === 'completed' ? 'Respondió' : 'Pendiente'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Dialog open={newCultureCampaignModalOpen} onOpenChange={setNewCultureCampaignModalOpen}>
            <DialogContent>
              <form onSubmit={handleCreateCultureCampaign}>
                <DialogHeader>
                  <DialogTitle>Nueva campaña de cultura</DialogTitle>
                  <DialogDescription>
                    Agrupa las evaluaciones de cultura de este periodo (por ejemplo &quot;Cultura Q1 2026&quot;). Los
                    resultados de campañas cerradas se conservan en el historial.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="culture-campaign-title">Nombre de la campaña</Label>
                  <Input
                    id="culture-campaign-title"
                    value={newCultureCampaignTitle}
                    onChange={(e) => setNewCultureCampaignTitle(e.target.value)}
                    placeholder="Ej. Cultura febrero 2026"
                    className="mt-2"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setNewCultureCampaignModalOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={!newCultureCampaignTitle.trim() || creatingCultureCampaign}>
                    {creatingCultureCampaign ? 'Creando…' : 'Crear campaña'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={cultureHistoryModalOpen} onOpenChange={setCultureHistoryModalOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Historial de campañas de cultura</DialogTitle>
                <DialogDescription>Campañas cerradas. Consulta resultados por periodo.</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                {loadingCultureHistory ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Cargando historial...</p>
                ) : closedCultureCampaigns.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">No hay campañas cerradas.</p>
                ) : (
                  <div className="max-h-72 overflow-auto rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-2.5 text-left font-medium">Campaña</th>
                          <th className="px-4 py-2.5 text-left font-medium">Inicio</th>
                          <th className="px-4 py-2.5 text-left font-medium">Cierre</th>
                          <th className="px-4 py-2.5 font-medium" />
                        </tr>
                      </thead>
                      <tbody>
                        {closedCultureCampaigns.map((c) => (
                          <tr key={c.id} className="border-b last:border-0">
                            <td className="px-4 py-2.5 font-medium">{c.title}</td>
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {c.createdAt
                                ? new Date(c.createdAt).toLocaleDateString('es-MX', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {c.closedAt
                                ? new Date(c.closedAt).toLocaleDateString('es-MX', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : '—'}
                            </td>
                            <td className="px-4 py-2.5">
                              <Button size="sm" variant="outline" asChild>
                                <Link
                                  to={`/app/culture?campaignId=${c.id}`}
                                  onClick={() => setCultureHistoryModalOpen(false)}
                                >
                                  Ver resultados
                                </Link>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === 'papelera' && (
        <>
          {loading ? (
            <p className="text-muted-foreground">Cargando...</p>
          ) : employees.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trash2 className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <p className="text-muted-foreground">No hay empleados archivados</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Papelera de reciclaje</CardTitle>
                <CardDescription>Empleados archivados.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 pr-4 font-medium">Nombre</th>
                        <th className="pb-2 pr-4 font-medium">Fecha de baja</th>
                        <th className="pb-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.id} className="border-b last:border-0">
                          <td className="py-3 pr-4">
                            {emp.firstName} {emp.lastName}
                          </td>
                          <td className="py-3 pr-4">
                            {emp.archivedAt
                              ? new Date(emp.archivedAt).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : '—'}
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-green-600/50 text-green-700 hover:bg-green-50"
                                disabled={restoringId === emp.id}
                                onClick={() => handleRestore(emp)}
                              >
                                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                                {restoringId === emp.id ? 'Restaurando…' : 'Restaurar'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                                disabled={hardDeletingId === emp.id}
                                onClick={() => handleHardDelete(emp)}
                              >
                                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                {hardDeletingId === emp.id ? 'Eliminando…' : 'Eliminar Definitivamente'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === 'bitacora' && (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar empleados por nombre o área..."
              value={bitacoraSearch}
              onChange={(e) => setBitacoraSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bitácora de seguimiento conductual</CardTitle>
              <CardDescription>
                Haz clic en un empleado para registrar una nota de desempeño. Accede al historial desde el popover.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Cargando...</p>
              ) : bitacoraFilteredEmployees.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  {bitacoraSearch.trim() ? 'No hay empleados que coincidan con la búsqueda.' : 'No hay empleados en el directorio.'}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {bitacoraFilteredEmployees.map((emp) => (
                    <BitacoraEntryPopover key={emp.id} employee={emp}>
                      <span className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-muted/50 hover:border-primary/50">
                        <span className="font-medium">{emp.firstName} {emp.lastName}</span>
                        {emp.department && (
                          <span className="text-muted-foreground">({emp.department})</span>
                        )}
                      </span>
                    </BitacoraEntryPopover>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'evaluaciones' && (
        <div className="space-y-6">
          {/* Banner Campaña Activa NOM-035 */}
          {loadingCampaign ? (
            <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
              Cargando campaña...
            </div>
          ) : !activeCampaign ? (
            <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
              <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  No hay ninguna campaña de evaluación activa en este momento.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setHistoryModalOpen(true)}>
                    <History className="mr-2 h-4 w-4" />
                    Ver Historial
                  </Button>
                  <Button onClick={() => setNewCampaignModalOpen(true)}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Iniciar Nueva Campaña
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/30 dark:text-green-200">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                Campaña Activa: <strong>{activeCampaign.title}</strong>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryModalOpen(true)}
                  className="border-green-300 text-green-800 hover:bg-green-100 dark:border-green-700 dark:text-green-200 dark:hover:bg-green-900/50"
                >
                  <History className="mr-2 h-4 w-4" />
                  Ver Historial
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseCampaign}
                  disabled={closingCampaignId === activeCampaign.id}
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/50"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  {closingCampaignId === activeCampaign.id ? 'Cerrando…' : 'Cerrar Campaña'}
                </Button>
              </div>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="text-base">Centro de Reportes NOM-035</CardTitle>
                  <CardDescription>
                    Dashboard global e individual para cumplimiento ante inspecciones STPS
                  </CardDescription>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="shrink-0"
                  disabled={isGeneratingGlobalPdf || latestAssessments.length === 0}
                  onClick={handleDownloadGlobalPdf}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  {isGeneratingGlobalPdf ? 'Generando…' : '📄 Descargar Reporte Global (PDF)'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Cargando reportes...</p>
              ) : (
                <>
                  {/* KPIs */}
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border bg-card p-4">
                      <p className="text-2xl font-semibold tabular-nums">
                        {employees.length > 0
                          ? Math.min(
                              100,
                              Math.round((latestAssessments.length / employees.length) * 100)
                            )
                          : 0}
                        %
                      </p>
                      <p className="text-sm text-muted-foreground">Participación</p>
                    </div>
                    <div className="rounded-lg border bg-card p-4">
                      <p className="text-2xl font-semibold tabular-nums">
                        {latestAssessments.length}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Evaluados</p>
                    </div>
                    <div className="rounded-lg border bg-card p-4">
                      <p
                        className={cn(
                          'text-2xl font-semibold tabular-nums',
                          latestAssessments.filter((a) => a.requiresClinicalAttention === true).length > 0 &&
                            'text-destructive'
                        )}
                      >
                        {latestAssessments.filter((a) => a.requiresClinicalAttention === true).length}
                      </p>
                      <p className="text-sm text-muted-foreground">Atención Clínica Requerida</p>
                    </div>
                  </div>

                  {/* Tabla de Resultados Individuales */}
                  <div className="mt-6">
                    <h3 className="mb-3 text-sm font-semibold">Resultados individuales</h3>
                    <div className="overflow-x-auto rounded-md border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="px-4 py-2.5 text-left font-medium">Empleado</th>
                            <th className="px-4 py-2.5 text-left font-medium">Fecha de Evaluación</th>
                            <th className="px-4 py-2.5 text-left font-medium">Tipo de Guía</th>
                            <th className="px-4 py-2.5 text-left font-medium">Resultado</th>
                            <th className="px-4 py-2.5 font-medium"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {latestAssessments.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                No hay evaluaciones completadas. Envía invitaciones desde el Directorio.
                              </td>
                            </tr>
                          ) : (
                            latestAssessments.map((a) => (
                              <tr key={a.id} className="border-b last:border-0">
                                <td className="px-4 py-2.5 font-medium">{a.employeeName}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">
                                  {a.completedAt
                                    ? new Date(a.completedAt).toLocaleDateString('es-MX', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '—'}
                                </td>
                                <td className="px-4 py-2.5">
                                  {a.guideType === 'NOM35_G1' && 'Guía I (ATS)'}
                                  {a.guideType === 'NOM35_G2' && 'Guía II'}
                                  {a.guideType === 'NOM35_G3' && 'Guía III'}
                                </td>
                                <td className="px-4 py-2.5">
                                  {a.guideType === 'NOM35_G1' ? (
                                    a.requiresClinicalAttention === true ? (
                                      <Badge variant="muyAlto">Requiere Atención Clínica</Badge>
                                    ) : (
                                      <Badge variant="bajo">Sin Riesgo</Badge>
                                    )
                                  ) : (
                                    <Badge variant={getRiskLevelVariant(a.resultLevel)}>
                                      {a.resultLevel ?? '—'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="px-4 py-2.5">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenExpediente(a.employeeId, a.employeeName)}
                                  >
                                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                                    Ver Detalle
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={addModalOpen}
        onOpenChange={(open) => {
          setAddModalOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{editingId ? 'Editar empleado' : 'Agregar empleado'}</h2>
            <form onSubmit={handleSaveEmployee} className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Nombre</Label>
                  <Input id="firstName" value={formFirstName} onChange={(e) => setFormFirstName(e.target.value)} placeholder="Juan" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Apellido</Label>
                  <Input id="lastName" value={formLastName} onChange={(e) => setFormLastName(e.target.value)} placeholder="Pérez" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="juan@empresa.com" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <div
                  className="[&_.PhoneInput]:flex [&_.PhoneInput]:gap-2 [&_.PhoneInputInput]:flex [&_.PhoneInputInput]:h-10 [&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:rounded-md [&_.PhoneInputInput]:border [&_.PhoneInputInput]:border-input [&_.PhoneInputInput]:bg-background [&_.PhoneInputInput]:px-3 [&_.PhoneInputInput]:py-2 [&_.PhoneInputInput]:text-sm [&_.PhoneInputCountryIcon]:!h-5 [&_.PhoneInputCountryIcon]:!w-[1.875rem]"
                >
                  <PhoneInput defaultCountry="MX" value={formPhone} onChange={(val) => setFormPhone(val ?? '')} placeholder="+52 55 1234 5678" />
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="position">Puesto</Label>
                  <Input id="position" value={formPosition} onChange={(e) => setFormPosition(e.target.value)} placeholder="Analista" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Departamento</Label>
                  <Input id="department" value={formDepartment} onChange={(e) => setFormDepartment(e.target.value)} placeholder="Ventas" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateOfHire">Fecha de ingreso</Label>
                <Input id="dateOfHire" type="date" value={formDateOfHire} onChange={(e) => setFormDateOfHire(e.target.value)} />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="sex">Sexo</Label>
                  <select
                    id="sex"
                    value={formSex}
                    onChange={(e) => setFormSex(e.target.value)}
                    className={cn(
                      'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                  >
                    <option value="">Sin especificar</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthDate">Fecha de nacimiento</Label>
                  <Input id="birthDate" type="date" value={formBirthDate} onChange={(e) => setFormBirthDate(e.target.value)} />
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="maritalStatus">Estado civil</Label>
                  <Input
                    id="maritalStatus"
                    value={formMaritalStatus}
                    onChange={(e) => setFormMaritalStatus(e.target.value)}
                    placeholder="Ej. Soltero(a)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="educationLevel">Escolaridad</Label>
                  <Input
                    id="educationLevel"
                    value={formEducationLevel}
                    onChange={(e) => setFormEducationLevel(e.target.value)}
                    placeholder="Ej. Licenciatura"
                  />
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="jobType">Tipo de puesto</Label>
                  <Input
                    id="jobType"
                    value={formJobType}
                    onChange={(e) => setFormJobType(e.target.value)}
                    placeholder="Ej. Operativo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contractType">Tipo de contrato</Label>
                  <Input
                    id="contractType"
                    value={formContractType}
                    onChange={(e) => setFormContractType(e.target.value)}
                    placeholder="Ej. Indefinido"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setAddModalOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={formSubmitting}>
                  {formSubmitting ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Agregar'}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resultsModalOpen}
        onOpenChange={(open) => {
          setResultsModalOpen(open);
          if (!open) {
            setCurrentResults(null);
            setSelectedEmployeeName('');
            setSelectedEmployee(null);
            setResultsError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl print:max-w-none">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Expediente Histórico NOM-035</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Resultados de {selectedEmployeeName || '—'}
                </p>
              </div>
              {(currentResults?.length ?? 0) > 0 && (
                <Button
                  type="button"
                  className="shrink-0"
                  disabled={isGeneratingExpedientePdf}
                  onClick={handleDownloadExpedientePdf}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  {isGeneratingExpedientePdf ? 'Generando…' : '📄 Descargar Expediente (PDF)'}
                </Button>
              )}
            </div>

            {loadingResults ? (
              <p className="text-muted-foreground">Buscando resultados...</p>
            ) : resultsError ? (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                <p>{resultsError}</p>
              </div>
            ) : !currentResults || currentResults.length === 0 ? (
              <p className="text-muted-foreground">No hay evaluaciones completadas.</p>
            ) : (
              <div className="space-y-3">
                {currentResults.map((assessment) => (
                  <Card key={assessment.id}>
                    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">
                          {assessment.completedAt
                            ? new Date(assessment.completedAt).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </p>
                        <p className="text-sm font-medium">
                          {assessment.guideType === 'NOM35_G1' && 'Guía de Referencia I (ATS)'}
                          {assessment.guideType === 'NOM35_G2' && 'Guía de Referencia II'}
                          {assessment.guideType === 'NOM35_G3' && 'Guía de Referencia III'}
                        </p>
                        <div>
                          {assessment.guideType === 'NOM35_G1' ? (
                            assessment.requiresClinicalAttention === true ? (
                              <Badge variant="muyAlto">Requiere Atención</Badge>
                            ) : (
                              <Badge variant="bajo">Sin Riesgo</Badge>
                            )
                          ) : (
                            <Badge variant={getRiskLevelVariant(assessment.resultLevel)}>
                              {assessment.resultLevel ?? '—'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end print:hidden">
            <Button variant="outline" onClick={() => setResultsModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Historial de Campañas</DialogTitle>
              <DialogDescription>
                Campañas de evaluación NOM-035 cerradas anteriormente.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {loadingHistory ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Cargando historial...</p>
              ) : historyError ? (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                  <p>{historyError}</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => loadHistory()}>
                    Reintentar
                  </Button>
                </div>
              ) : closedCampaigns.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No hay campañas anteriores registradas.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-left font-medium">Campaña</th>
                        <th className="px-4 py-2.5 text-left font-medium">Fecha de inicio</th>
                        <th className="px-4 py-2.5 text-left font-medium">Fecha de cierre</th>
                        <th className="px-4 py-2.5 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {closedCampaigns.map((c) => (
                        <tr key={c.id} className="border-b last:border-0">
                          <td className="px-4 py-2.5 font-medium">{c.title}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {c.createdAt
                              ? new Date(c.createdAt).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {c.closedAt
                              ? new Date(c.closedAt).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <Button size="sm" variant="outline" asChild>
                              <Link to={`/app/nom035/results/${c.id}`}>Ver Resultados</Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {historyEmployee && companyIdOrNull && (
        <EmployeeHistoryDialog
          open={historyDialogOpen}
          onOpenChange={(open) => {
            setHistoryDialogOpen(open);
            if (!open) setHistoryEmployee(null);
          }}
          employeeId={historyEmployee.id}
          employeeName={historyEmployee.name}
          companyId={companyIdOrNull}
          onViewNom35Report={(empId, empName) => {
            setHistoryDialogOpen(false);
            setHistoryEmployee(null);
            handleOpenExpediente(empId, empName);
          }}
        />
      )}
    </div>
  );
}
